// Owns the orbit target across Large Board toggles.
//
// - On mount: seeds the orbit target with the (already board-scaled) initial
//   framing point. drei's OrbitControls `target` prop is deliberately NOT
//   used — it re-applies on re-render and would fight the player's pans.
// - On scale change: multiplies the camera's offset from the target by the
//   same ratio as the board, so the toggle reads as "everything got bigger"
//   rather than "the camera teleported". The initial Canvas camera position
//   is exempt — Scene computes it with the scale already applied.

import { useThree } from '@react-three/fiber';
import { type RefObject, useEffect, useRef } from 'react';
import type { OrbitControlsLike } from '../cinematics/CinematicCameraRig.js';

export function BoardScaleRefit({
  scale,
  initialTarget,
  controls,
}: {
  readonly scale: number;
  readonly initialTarget: readonly [number, number, number];
  readonly controls: RefObject<(OrbitControlsLike & { update(): boolean }) | null>;
}) {
  const camera = useThree((s) => s.camera);
  const applied = useRef<number | null>(null);
  useEffect(() => {
    const ctl = controls.current;
    if (!ctl) return;
    const prev = applied.current;
    applied.current = scale;
    if (prev === null) {
      ctl.target.set(initialTarget[0], initialTarget[1], initialTarget[2]);
      ctl.update();
      return;
    }
    if (prev === scale) return;
    const ratio = scale / prev;
    ctl.target.multiplyScalar(ratio);
    camera.position.multiplyScalar(ratio);
    ctl.update();
  }, [scale, initialTarget, camera, controls]);
  return null;
}
